import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { OfferteWizard } from "@/components/site/OfferteWizard";
import maikelAmsterdam from "@/assets/maikel-amsterdam.png.asset.json";
import dannyRotterdam from "@/assets/danny-rotterdam.png.asset.json";
import basTilburg from "@/assets/bas-tilburg.png.asset.json";
import erwinNijmegen from "@/assets/erwin-nijmegen.png.asset.json";
import svenAlmere from "@/assets/sven-almere.png.asset.json";
import timUtrecht from "@/assets/tim-utrecht.png.asset.json";
import jeffreyEindhoven from "@/assets/jeffrey-eindhoven.png.asset.json";
import rickDenHaag from "@/assets/rick-den-haag.png.asset.json";
import kevinArnhem from "@/assets/kevin-arnhem.png.asset.json";
import { CITY_CONTENT, GENERIC_FAQ, type CitySlug } from "@/lib/groepenkast-cities";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { serviceJsonLd } from "@/lib/service-jsonld";
import { groepenkastProductJsonLd } from "@/lib/groepenkast-product-jsonld";

const INSTALLER_IMAGES: Record<string, { url: string; objectPosition?: string }> = {
  amsterdam: { url: maikelAmsterdam.url, objectPosition: "top" },
  rotterdam: { url: dannyRotterdam.url, objectPosition: "center" },
  tilburg: { url: basTilburg.url, objectPosition: "center" },
  nijmegen: { url: erwinNijmegen.url, objectPosition: "center" },
  almere: { url: svenAlmere.url, objectPosition: "center" },
  utrecht: { url: timUtrecht.url, objectPosition: "center" },
  eindhoven: { url: jeffreyEindhoven.url, objectPosition: "50% 20%" },
  "den-haag": { url: rickDenHaag.url, objectPosition: "50% 30%" },
  arnhem: { url: kevinArnhem.url, objectPosition: "50% 20%" },
};
import {
  CheckCircle2,
  Check,
  ShieldCheck,
  MapPin,
  Phone,
  Clock,
  ClipboardCheck,
  Wrench,
  FileCheck2,
  MessageSquare,
  Star,
  BatteryCharging,
  Cable,
  ShieldAlert,
} from "lucide-react";


const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/groepenkast-vervangen/$stad")({
  loader: ({ params }) => {
    const city = CITY_CONTENT[params.stad as CitySlug];
    if (!city) throw notFound();
    return { city };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Niet gevonden | Groepenkast vervangen" }, { name: "robots", content: "noindex" }] };
    }
    const city = loaderData.city;
    const url = `${SITE_URL}/groepenkast-vervangen/${params.stad}`;
    const faq = [...GENERIC_FAQ, ...(city.localFaq ?? [])];
    return {
      meta: [
        { title: city.metaTitle },
        { name: "description", content: city.metaDescription },
        { property: "og:title", content: city.metaTitle },
        { property: "og:description", content: city.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "nl_NL" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
        { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Archivo+Black&family=Hind:wght@400;500;600;700&display=swap" },
      ],
      scripts: [
        breadcrumbJsonLd([
          { name: "Groepenkast vervangen", path: "/groepenkast-vervangen" },
          { name: city.name, path: `/groepenkast-vervangen/${params.stad}` },
        ]),
        serviceJsonLd({
          path: `/groepenkast-vervangen/${params.stad}`,
          name: `Groepenkast vervangen in ${city.name}`,
          description: city.metaDescription,
          serviceType: "Groepenkast vervangen",
          areaServed: city.name,
          offerCatalog: [
            `Groepenkast vervangen ${city.name}`,
            "Uitbreiding groepenkast",
            "Aardlekautomaten plaatsen",
            "Meterkast ombouwen 1-fase → 3-fase",
            "Spoedservice groepenkast",
          ],
        }),
        groepenkastProductJsonLd({
          url,
          name: `Groepenkast vervangen in ${city.name}`,
          description: city.metaDescription,
        }),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: CityPage,
  notFoundComponent: () => (
    <div className="gk-page min-h-screen flex flex-col bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-3xl font-bold">Locatie niet gevonden</h1>
        <p className="mt-4 text-muted-foreground">Vraag ook zonder locatiepagina een offerte aan.</p>
        <Link to="/offerte-aanvragen" search={{ product: "groepenkast" }} className="mt-6 inline-block text-orange underline">
          Naar offerte groepenkasten
        </Link>
      </main>
      <Footer />
    </div>
  ),
});

const KAST_INHOUD = [
  { num: 1, icon: ShieldAlert, title: "Hoofdschakelaar", text: "Schakelt de complete groepenkast in één keer spanningsvrij, essentieel bij storing of onderhoud." },
  { num: 2, icon: ShieldCheck, title: "Aardlekautomaat per 4 groepen", text: "Combineert aardlek- en overstroombeveiliging per 4 groepen, zodat bij een fout slechts een deel uitvalt." },
  { num: 3, icon: Cable, title: "Automaten (B16 / C16 / C20)", text: "Beveiliging tegen overbelasting en kortsluiting, per groep afgestemd op het verwachte vermogen." },
  { num: 4, icon: BatteryCharging, title: "Overspanningsbeveiliging", text: "Beschermt elektronica tegen bliksem en netpieken, steeds vaker verplicht bij nieuwe installaties." },
];

const STEPS = [
  { icon: MessageSquare, title: "1. Aanvraag", text: "U vult de wizard in of belt ons. We stemmen wensen en situatie kort af, meestal zelfde dag." },
  { icon: ClipboardCheck, title: "2. Vaste offerte", text: "Binnen 1 werkdag ontvangt u een vaste prijs, géén verrassingen achteraf, alles op papier." },
  { icon: Clock, title: "3. Inplannen", text: "We plannen een dagdeel dat u uitkomt. Doorgaans binnen 1–2 weken beschikbaar." },
  { icon: Wrench, title: "4. Vervanging", text: "Onze NEN 1010-monteur vervangt de kast in doorgaans 3–6 uur. U bent kort zonder stroom." },
  { icon: FileCheck2, title: "5. Oplevering & keuring", text: "Wij testen, verzegelen (Liander/Stedin) en leveren keuringsrapport + 15 jaar garantie op AEG." },
];

function CityPage() {
  const { city } = Route.useLoaderData();
  const { stad } = Route.useParams();
  const installerImage = INSTALLER_IMAGES[stad] ?? INSTALLER_IMAGES.amsterdam;


  return (
    <div className="min-h-screen flex flex-col bg-white gk-page">
      <Header />
      <main className="flex-1">
        {/* Scoped fonts + tokens voor deze pagina */}
        <style>{`
          .gk-page { font-family: 'Hind', system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #0f172a; }
          .gk-page h1, .gk-page h2, .gk-page h3, .gk-page .gk-display {
            font-family: 'Archivo Black', system-ui, sans-serif; letter-spacing: -0.01em;
          }
          .gk-page .text-orange,
          .gk-page .text-primary { color: #1e3679; }
          .gk-page .hover\:text-orange:hover,
          .gk-page .hover\:text-primary:hover { color: #1e3679; }

          /* Header accentkleuren naar #1e3679 */
          .gk-page header .bg-orange { background-color: #1e3679; }
          .gk-page header .hover\:bg-orange\/90:hover { background-color: #162b5f; }

          .gk-page header .bg-orange\/10 { background-color: rgb(30 54 121 / 0.10); }
          .gk-page header .hover\:bg-orange\/10:hover { background-color: rgb(30 54 121 / 0.10); }
          .gk-page header .focus-visible\:ring-orange:focus-visible { --tw-ring-color: #1e3679; }
          .gk-page header .focus-within\:border-orange:focus-within { border-color: #1e3679; }
          .gk-page header .focus-within\:ring-orange\/30:focus-within { --tw-ring-color: rgb(30 54 121 / 0.30); }

          /* Lichtblauwe cart-knop in header */
          .gk-page header [data-tour="cart"] button {
            background-color: #1e3679 !important;
            border-color: #1e3679 !important;
            color: #ffffff !important;
          }
          .gk-page header [data-tour="cart"] button:hover {
            background-color: #162b5f !important;
            border-color: #162b5f !important;
          }
        `}</style>


        {/* Installateur intro card */}
        {(() => {
          const inst = city.installer ?? {
            name: "Onze installateur",
            years: "meerdere jaren",
            experience: "ruime ervaring met groepenkasten",
            bio1: `Onze installateur werkt al meerdere jaren bij ons en is thuis in heel ${city.name} en omgeving.`,
            bio2: `"Ik vind het mooi om ouderwetse installaties in ${city.name} klaar te maken voor de toekomst, denk aan zonnepanelen, warmtepomp of laadpaal."`,
          };
          return (
            <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
              <div className="grid overflow-hidden rounded-2xl border border-slate-200 shadow-sm lg:grid-cols-2" style={{ backgroundColor: '#F1F5F8' }}>
                <div className="flex flex-col justify-center p-6 sm:p-10 lg:p-12">
                  <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">{inst.name}, uw installateur in {city.name}</h2>
                  <p className="mt-4 hidden text-base leading-relaxed text-slate-700 lg:block">{inst.bio1}</p>
                  <p className="mt-3 hidden text-base leading-relaxed text-slate-700 lg:block">{inst.bio2}</p>
                  <ul className="mt-6 space-y-2 text-sm text-slate-700">
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-600" /> NEN 1010 gecertificeerd monteur</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-600" /> Kent iedere buurt in {city.name}</li>
                    <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-600" /> {inst.experience}</li>
                  </ul>
                </div>
                <div className="relative min-h-[320px] bg-slate-100 lg:min-h-[480px]">
                  <img
                    src={installerImage.url}
                    alt={`${inst.name}, installateur in ${city.name}`}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ objectPosition: installerImage.objectPosition ?? "top" }}
                    loading="lazy"
                  />
                </div>
              </div>
            </section>
          );
        })()}


        {/* Split-screen HERO */}

        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white" />
            <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-orange/10 blur-3xl" />
            <div className="absolute top-40 -left-32 h-96 w-96 rounded-full bg-blue-100/60 blur-3xl" />
          </div>

          <div className="mx-auto grid max-w-7xl gap-12 px-4 pt-10 pb-14 sm:px-6 lg:grid-cols-[1.05fr_minmax(420px,480px)] lg:gap-14 lg:px-8 lg:pt-14 lg:pb-20">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 shadow-sm">
                <MapPin className="h-3.5 w-3.5 text-orange" /> Installateur regio {city.name}
              </div>
              <h1 className="text-4xl leading-[1.05] tracking-tight text-slate-900 md:text-5xl lg:text-6xl">
                Groepenkast vervangen in <span className="text-orange">{city.name}</span>
              </h1>
              <p className="mt-5 max-w-xl text-base text-slate-600 md:text-lg">{city.heroIntro}</p>

              <ul className="mt-7 grid gap-2.5 sm:grid-cols-2">
                {[
                  "15 jaar AEG-garantie op werking",
                  "2 jaar garantie op ons installatiewerk",
                  "Vaste prijs vooraf, geen meerwerk",
                  "Offerte binnen 24 uur",
                  "In één werkdag klaar",
                  "Zegelbevoegd Liander & Stedin",
                ].map((u) => (
                  <li key={u} className="flex items-start gap-2 text-sm md:text-base text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                    <span>{u}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href="tel:0850806695" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800">
                  <Phone className="h-4 w-4" /> Bel direct
                </a>
                <Link to="/offerte-aanvragen" search={{ product: "groepenkast" }} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 hover:border-slate-400">
                  Vraag offerte aan
                </Link>
              </div>

              <div className="mt-8 flex items-center gap-3 text-sm text-slate-600">
                <div className="flex items-center gap-0.5 text-amber-500">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                </div>
                <span><strong className="text-slate-900">4,9 / 5</strong>, beoordeeld door klanten in {city.name} en omstreken</span>
              </div>
            </div>

            <div id="offerte">
              <OfferteWizard city={city.name} />
            </div>
          </div>
        </section>



        {/* Wat zit er in de kast */}
        <section className="border-t border-slate-100 bg-slate-50/60">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:items-stretch">
              <div className="flex flex-col justify-between">
                <h2 className="text-3xl md:text-4xl text-slate-900">Wat zit er in een moderne groepenkast?</h2>
                <div className="mt-8 space-y-4">
                  <p className="text-slate-600">
                    Een moderne AEG-groepenkast is meer dan wat schakelaars. Iedere component beschermt uw woning en maakt uw installatie klaar voor de energietransitie.
                  </p>
                  <p className="text-slate-600">
                    In {city.name} zien we vaak nog kasten uit de jaren '70 en '80 met alleen smeltzekeringen. Die missen essentiële beveiligingen die vandaag standaard zijn.
                  </p>
                  <p className="text-slate-600">
                    Elke groepenkast die wij plaatsen wordt volledig volgens de NEN1010-norm geïnstalleerd. Deze norm beschrijft precies hoe een elektrische installatie in Nederland veilig en betrouwbaar moet worden opgeleverd.
                  </p>
                  <p className="text-slate-600">
                    Verzekeraars vragen steeds vaker om een installatie die voldoet aan de NEN1010. Bij brand- of waterschade kan een verouderde of niet-normconforme groepenkast ertoe leiden dat een schadeclaim (deels) wordt afgewezen. Met een door ons geplaatste, goed gedocumenteerde kast voorkomt u dat risico.
                  </p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {KAST_INHOUD.map((k) => (
                  <div key={k.title} className="relative rounded-xl border border-slate-200 bg-white p-5">
                    <span className="absolute top-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-orange/10 text-xl font-bold text-orange gk-display">
                      {k.num}
                    </span>
                    <k.icon className="h-6 w-6 text-orange" />
                    <h3 className="mt-3 text-base text-slate-900">{k.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-600">{k.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stappenplan */}
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl">Zo werkt het, van offerte tot oplevering</h2>
            <p className="mt-3 text-slate-600">Vijf stappen. Duidelijk en zonder verrassingen.</p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-5">
            {STEPS.map((s) => (
              <div key={s.title} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange/10 text-orange">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base text-slate-900">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SEO body + signalen */}
        <section className="border-t border-slate-100 bg-slate-50/60">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl text-slate-900">Betrouwbare groepenkast-installateur in {city.name}</h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-700">
              {city.body.map((para: string, i: number) => (<p key={i}>{para}</p>))}
            </div>

            <h3 className="mt-10 text-2xl text-slate-900">Wanneer is vervangen nodig in {city.name}?</h3>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {city.signals.map((s: string) => (
                <li key={s} className="flex items-start gap-2 text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>

            <h3 className="mt-10 text-2xl text-slate-900">Werkgebied rondom {city.name}</h3>
            <p className="mt-3 text-slate-700">
              Onze installateurs zijn ook actief in {city.neighborhoods.join(", ")} en omliggende gemeenten.
            </p>
          </div>
        </section>

        {/* Klantverhalen */}
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-2xl text-left">
            <h2 className="text-3xl md:text-4xl">Klanten uit {city.name} aan het woord</h2>
            <p className="mt-3 text-slate-600">Echte reviews van woningeigenaren in uw regio.</p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {city.reviews.map((r: typeof city.reviews[number]) => (
              <div key={r.name} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-0.5 text-amber-500">
                  {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">"{r.text}"</p>
                <div className="mt-4 border-t border-slate-100 pt-3 text-sm">
                  <div className="font-bold text-slate-900">{r.name}</div>
                  <div className="text-slate-500">{r.neighborhood}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-slate-100 bg-slate-50/60">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="max-w-2xl text-left">
              <h2 className="text-3xl md:text-4xl">Veelgestelde vragen</h2>
              <p className="mt-3 text-slate-600">Antwoorden op de vragen die klanten in {city.name} het vaakst stellen.</p>
            </div>
            <div className="mt-8 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
              {[...GENERIC_FAQ, ...(city.localFaq ?? [])].map((f) => (
                <details key={f.q} className="group px-5 py-4 open:bg-slate-50/60">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-bold text-slate-900">
                    {f.q}
                    <span className="mt-1 text-orange transition group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Interne links: andere steden + aanbiedingen */}
        <section aria-labelledby="andere-steden" className="border-t border-slate-100 bg-slate-50">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <h2 id="andere-steden" className="font-display text-2xl font-bold text-foreground">
              Groepenkast vervangen in andere steden
            </h2>
            <p className="mt-2 text-text-secondary">
              Bekijk onze <Link to="/groepenkast-vervangen" className="font-semibold text-orange hover:underline">volledige stedenoverzicht</Link>,
              onze <Link to="/dekking" className="font-semibold text-orange hover:underline">dekking</Link> of
              onze actuele <Link to="/aanbiedingen" className="font-semibold text-orange hover:underline">aanbiedingen</Link>.
            </p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {Object.entries(CITY_CONTENT)
                .filter(([slug]) => slug !== stad)
                .map(([slug, c]) => (
                  <li key={slug}>
                    <Link
                      to="/groepenkast-vervangen/$stad"
                      params={{ stad: slug }}
                      className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-foreground transition hover:border-orange hover:text-orange"
                    >
                      Groepenkast vervangen {c.name}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
        </section>

        {/* Slot CTA - licht */}
        <section className="border-t border-slate-100 bg-white">

          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 px-6 py-12 text-center text-white shadow-xl sm:px-12">
              <h2 className="text-3xl md:text-4xl">Klaar voor een veilige groepenkast in {city.name}?</h2>
              <p className="mx-auto mt-7 max-w-2xl text-white/80">
                Vraag gratis en vrijblijvend een offerte op maat aan. U ontvangt binnen 24 uur een zeer gunstige offerte met duidelijke prijzen, A-Merk AEG componenten en natuurlijk de uitstekende garantie van maar liefst 15 jaar.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <a href="tel:0850806695" className="inline-flex items-center gap-2 rounded-full bg-orange px-6 py-3 text-sm font-bold text-white hover:opacity-90">
                  <Phone className="h-4 w-4" /> 085 080 66 95
                </a>
                <Link to="/offerte-aanvragen" search={{ product: "groepenkast" }} className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10">
                  Offerte aanvragen
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
